const allowedSorts = ['recommended', 'updated']

function normalizeArray(values) {
  if (!Array.isArray(values)) return []
  return values
    .map(value => (typeof value === 'string' ? value.trim() : value))
    .filter(value => value !== '' && value !== null && value !== undefined)
}

function positiveInteger(value, fallback) {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

export function restoreLeaderHunterSearchState(savedValue, defaults) {
  const result = { ...defaults }
  let saved = savedValue
  if (typeof savedValue === 'string' && savedValue) saved = JSON.parse(savedValue)
  if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return result

  Object.keys(result).forEach(key => {
    if (saved[key] !== undefined) {
      result[key] = Array.isArray(saved[key]) ? [...saved[key]] : saved[key]
    }
  })
  return result
}

export function serializeLeaderHunterSearchState(query, defaults) {
  const persistable = restoreLeaderHunterSearchState(query, defaults)
  return JSON.stringify(persistable)
}

export function buildLeaderProfileSearchParams(query = {}) {
  const keyword = typeof query.keyword === 'string' ? query.keyword.trim().slice(0, 50) : ''
  const params = {
    keyword,
    role: query.role === 'leader' || query.role === 'guide' ? query.role : '',
    genders: normalizeArray(query.genders),
    ageRanges: normalizeArray(query.ageRanges),
    guidanceAreas: normalizeArray(query.guidanceAreas),
    guidanceYearRanges: normalizeArray(query.guidanceYearRanges),
    residences: normalizeArray(query.residences),
    availableDate: typeof query.availableDate === 'string' ? query.availableDate : '',
    sort: allowedSorts.includes(query.sort) ? query.sort : 'recommended',
    page: positiveInteger(query.page, 1),
    limit: Math.min(positiveInteger(query.limit, 20), 100),
  }

  if (typeof query.associationMember === 'boolean') {
    params.associationMember = query.associationMember
  }

  return params
}

const invitationStatusMap = {
  0: 'Pending',
  1: 'Accepted',
  2: 'Rejected',
  3: 'Cancelled',
  4: 'Expired',
}

const invitationLabels = {
  Pending: '等待回覆',
  Accepted: '已接受',
  Rejected: '已拒絕',
  Cancelled: '已撤回',
  Expired: '已過期',
}

const invitationTagTypes = {
  Pending: 'warning',
  Accepted: 'success',
  Rejected: 'danger',
  Cancelled: 'info',
  Expired: 'info',
}

export function normalizeInvitationStatus(status) {
  if (typeof status === 'number') return invitationStatusMap[status] || 'Unknown'
  const normalized = typeof status === 'string' ? status.trim().toLowerCase() : ''
  return Object.keys(invitationLabels).find(key => key.toLowerCase() === normalized) || 'Unknown'
}

export function invitationStatusLabel(status) {
  return invitationLabels[normalizeInvitationStatus(status)] || '未知狀態'
}

export function invitationStatusType(status) {
  return invitationTagTypes[normalizeInvitationStatus(status)] || 'info'
}

export function canCancelInvitation(invitation) {
  return normalizeInvitationStatus(invitation && invitation.status) === 'Pending'
}

export function isConversationReadOnly(conversation) {
  return !conversation || conversation.readOnly !== false
}

export function createConversationRequestGuard() {
  let generation = 0
  let activeConversationId = null

  return {
    begin(conversationId) {
      generation += 1
      activeConversationId = String(conversationId)
      return { generation, conversationId: activeConversationId }
    },
    isCurrent(request) {
      return Boolean(
        request &&
          request.generation === generation &&
          request.conversationId === activeConversationId
      )
    },
    invalidate() {
      generation += 1
      activeConversationId = null
    },
  }
}

export function createLatestConversationListRequestGuard() {
  let serial = 0
  return {
    begin() {
      serial += 1
      return serial
    },
    isCurrent(requestSerial) {
      return requestSerial === serial
    },
    invalidate() {
      serial += 1
    },
  }
}

export function findConversationByTrustedId(conversations, conversationId) {
  if (conversationId === undefined || conversationId === null || conversationId === '') {
    return null
  }
  const trustedId = String(conversationId)
  return (
    (Array.isArray(conversations) ? conversations : []).find(conversation =>
      [
        conversation && conversation.id,
        conversation && conversation.conversationId,
        conversation && conversation.roomId,
      ]
        .filter(id => id !== undefined && id !== null && id !== '')
        .some(id => String(id) === trustedId)
    ) || null
  )
}

export async function refreshCurrentConversationReadState({ loadMessages, markRead, isCurrent }) {
  const loaded = await loadMessages()
  if (!loaded || !isCurrent()) {
    return { refreshed: false, markedRead: false }
  }

  try {
    await markRead()
  } catch (error) {
    return { refreshed: true, markedRead: false, error }
  }
  if (!isCurrent()) {
    return { refreshed: true, markedRead: false }
  }
  return { refreshed: true, markedRead: true }
}

export function synchronizeConversationUnreadCount(conversations, conversationId, unreadCount = 0) {
  const targetId = String(conversationId)
  return (Array.isArray(conversations) ? conversations : []).map(conversation => {
    const currentId = String(
      conversation.id || conversation.conversationId || conversation.roomId || ''
    )
    return currentId === targetId ? { ...conversation, unreadCount } : conversation
  })
}

export function deliveredInvitation(result) {
  if (!result || typeof result !== 'object') return null
  const invitation = result.invitation || result
  const conversation = result.conversation
  const conversationId = invitation.conversationId || (conversation && conversation.id)

  if (!conversationId || !invitation.sentAt) return null
  if (conversation && !conversation.firstAgencyMessageAt) return null
  return invitation
}

export function normalizeLeaderProfile(profile) {
  if (!profile || typeof profile !== 'object') return profile
  const identityOptions = Number(profile.identityOptions || profile.IdentityOptions)
  const role = [1, 2].includes(identityOptions)
    ? 'leader'
    : [3, 4].includes(identityOptions)
      ? 'guide'
      : profile.role
  const rawRecommendSort =
    profile.recommendSort !== undefined ? profile.recommendSort : profile.RecommendSort

  return {
    ...profile,
    id: profile.id || profile.ID,
    userId: profile.userId || profile.UserId || profile.userID || profile.UserID,
    name: profile.name || profile.Name,
    nickName: profile.nickName || profile.NickName,
    role,
    associationMember:
      typeof profile.associationMember === 'boolean'
        ? profile.associationMember
        : [1, 3].includes(identityOptions),
    guidanceAreas: profile.guidanceAreas || profile.guidanceArea || profile.GuidanceArea || [],
    guidanceYears: profile.guidanceYears || profile.GuidanceYears,
    residence:
      profile.residence || profile.positionName || profile.PositionName || profile.position,
    recentTravels:
      profile.recentTravels || profile.travelsData || profile.TravelsData || profile.travels || [],
    isRecommended:
      (profile.isRecommended !== undefined ? profile.isRecommended : profile.IsRecommended) ===
      true,
    recommendSort: Number.isInteger(Number(rawRecommendSort)) ? Number(rawRecommendSort) : 99,
  }
}

export function normalizeAgencyConversation(conversation) {
  if (!conversation || typeof conversation !== 'object') return conversation
  return {
    ...conversation,
    lastMessagePreview:
      conversation.lastMessagePreview || conversation.lastMessage || conversation.LastMessage || '',
  }
}
